'use client';

import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { captureEvent, EVENTS } from '@/lib/posthog/events';
import { Loader2, MapPin, Mountain, Users, ChevronLeft, ChevronRight, Info, ChevronDown } from 'lucide-react';
import { cn } from '@sharpr/ui';
import { BookThumbnail } from '@/components/BookThumbnail';
import { useSlateData } from '@/hooks/useSlateData';
import { TeamLogo } from '../../TeamLogo';
import { getPlayerImageUrl } from '@/utils/playerImages';
import { ParkDiagram } from './ParkDiagram';
import { ParkFactorBars } from './ParkFactorBars';
// Lazy-loaded — pulls the full 167 KB venue-details.json. Defer until the
// user clicks a venue chip.
const VenueDetailModal = dynamic(
  () => import('./VenueDetailModal').then((m) => ({ default: m.VenueDetailModal })),
  { ssr: false },
);
// Lazy-loaded — pulls 12 tab modules + Recharts. Defer until first open.
const PlayerResearchOverlay = dynamic(
  () => import('../research/PlayerResearchOverlay').then((m) => ({ default: m.PlayerResearchOverlay })),
  { ssr: false },
);
import { useSport } from '@/contexts/SportContext';
// Slim park-factors slice (~4 KB) — full venue-details.json (~167 KB) ships
// only with the lazy-loaded VenueDetailModal. Regenerate via the inline
// codegen comment in slate-park-factors.json's parent script if upstream
// changes.
import slateParkFactors from '@/data/slate-park-factors.json';
import type { SlateGame } from '@/app/api/slate/route';
import type { ResearchTarget } from '@/types/research';
import { useMlbWeather } from '@/hooks/useMlbWeather';
import { useSlateMatchups, type TeamLineupRequest } from '@/hooks/useSlateMatchups';
import { useSlatePlayers, lineupKey } from '@/hooks/useSlatePlayers';
import { buildBatterFallbackTarget, buildPitcherFallbackTarget, resolveSwapTarget } from '@/lib/research/buildResearchTarget';
import type { MatchupBatter } from '@/hooks/useMatchupData';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { WeatherStrip } from '../weather/WeatherStrip';
import { PlayerDirectory } from './PlayerDirectory';
import { MarketOddsPopover } from './MarketOddsPopover';
import { resolveGameLines, type LinePreference, type ResolvedGameLines } from '@/utils/slateOddsBest';
import { utcToEasternDate, formatEtTime } from '@/lib/dateTime';
import type { GameWeather } from '@/services/weather/types';
import { formatOdds } from '@sharpr/ui/format';

// ─── Shared helpers ──────────────────────────────────────────

function BookIcon({ bookKey }: { bookKey?: string }) {
  return bookKey ? <BookThumbnail book={bookKey} size="sm" className="w-3.5 h-3.5 rounded-sm object-contain" /> : null;
}

function formatDateHeader(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return `Today, ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  if (d.toDateString() === tomorrow.toDateString()) return `Tomorrow, ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

function VenueTag({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span className={cn('text-[9px] px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5', color)}>
      {children}
    </span>
  );
}

type SlateParkFactors = Record<string, {
  runs: number | null; hr: number | null; hits: number | null;
  so: number | null; bb: number | null; triples: number | null;
  centerFt: number | null;
}>;

function getVenueData(homeAbbr: string) {
  const f = (slateParkFactors as SlateParkFactors)[homeAbbr];
  return {
    runsIdx: f?.runs ?? 100,
    hrIdx: f?.hr ?? 100,
    hitsIdx: f?.hits ?? 100,
    kIdx: f?.so ?? 100,
    bbIdx: f?.bb ?? 100,
    triplesIdx: f?.triples ?? 100,
    centerFt: f?.centerFt ?? null,
  };
}

function buildNarrative(game: SlateGame) {
  const v = game.venue;
  const isHighElevation = (v.elevation ?? 0) > 2000;
  const isDome = v.roof === 'Retractable' || v.roof === 'Dome';
  const isTurf = v.surface !== 'Grass';
  const { hrIdx, hitsIdx, kIdx, bbIdx, triplesIdx, centerFt } = getVenueData(game.home.abbr);

  const parts: string[] = [];
  if (isHighElevation) parts.push(`${v.elevation?.toLocaleString()}ft elevation — carry boost on fly balls`);
  if (hrIdx >= 115) parts.push('Strong HR park');
  else if (hrIdx >= 105) parts.push('Above-avg HR');
  else if (hrIdx <= 85) parts.push('Suppresses HR significantly');
  else if (hrIdx <= 95) parts.push('Below-avg HR');
  if (hitsIdx >= 107) parts.push('High hit environment');
  else if (hitsIdx <= 93) parts.push('Suppresses hits');
  if (kIdx >= 108) parts.push('Inflates strikeouts');
  else if (kIdx <= 92) parts.push('Suppresses strikeouts');
  if (bbIdx >= 108) parts.push('Walk-friendly');
  if (triplesIdx >= 150) parts.push('Triples-friendly (large OF)');
  const cf = centerFt != null ? Math.round(centerFt) : (v.dimensions.center ?? 400);
  if (cf <= 393) parts.push(`Short CF (${cf}ft)`);
  else if (cf >= 408) parts.push(`Deep CF (${cf}ft)`);
  if (isDome) parts.push('Controlled environment');
  if (isTurf) parts.push('Turf — faster ground balls');
  return parts;
}

function getVenueFlags(game: SlateGame) {
  const v = game.venue;
  const isHighElevation = (v.elevation ?? 0) > 2000;
  const isDome = v.roof === 'Retractable' || v.roof === 'Dome';
  const isTurf = v.surface !== 'Grass';
  const { runsIdx } = getVenueData(game.home.abbr);
  const isHitterPark = runsIdx > 103;
  const isPitcherPark = runsIdx < 97;
  return { isHighElevation, isDome, isTurf, isHitterPark, isPitcherPark };
}

function pitchingStatColor(value: number, goodThreshold: number, badThreshold: number, invert = false): string {
  const isGood = invert ? value >= goodThreshold : value <= goodThreshold;
  const isBad = invert ? value <= badThreshold : value >= badThreshold;
  if (isGood) return 'text-emerald-400';
  if (isBad) return 'text-red-400';
  return 'text-gray-200';
}

function eraColor(value: number): string {
  return pitchingStatColor(value, 3.50, 4.50);
}

function kPer9Color(value: number): string {
  return pitchingStatColor(value, 9.5, 7, true);
}

function whipColor(value: number): string {
  return pitchingStatColor(value, 1.15, 1.40);
}

// ─── Best-line resolution (client-side, honors user prefs) ──

const ML_PREF: LinePreference = 'point-irrelevant';
// Popover still uses preferences so users can visually scan alt points when they exist.
const RL_PREF: LinePreference = 'higher-point';
const TOTAL_OVER_PREF: LinePreference = 'lower-point';
const TOTAL_UNDER_PREF: LinePreference = 'higher-point';

// `resolveGameLines` lives in `@/utils/slateOddsBest` — shared with the
// line-movement overlay's GameContext block so both surfaces apply the same
// `excluded_from_best_line` filter to game-line picks.

const formatSpread = (point: number): string => (point > 0 ? `+${point}` : String(point));

// ─── Desktop components (unchanged) ─────────────────────────

function PitcherCard({ name, id, throws, record, stats, onOpenResearch }: {
  name: string;
  id?: number;
  throws?: 'L' | 'R';
  record?: { wins: number; losses: number };
  stats?: { era: number; fip?: number; k_per_9: number; whip: number; games: number };
  onOpenResearch?: () => void;
}) {
  const url = id ? getPlayerImageUrl(name, 'mlb', undefined, String(id)) : null;
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative w-11 h-11 flex-shrink-0">
        <div className="w-11 h-11 rounded-full overflow-hidden bg-muted dark:bg-bg-tertiary border border-border/30 dark:border-white/10">
          {url ? (
            <Image src={url} alt={name} width={44} height={44} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[11px] font-bold text-gray-400">
              {name.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </div>
          )}
        </div>
        {record && (
          <span className="absolute left-1/2 -translate-x-1/2 -bottom-3.5 text-[10px] font-mono text-gray-400 leading-none whitespace-nowrap">
            {record.wins}-{record.losses}
          </span>
        )}
      </div>
      <div className="min-w-0">
        <div className="text-[13px] font-semibold truncate">
          <span
            className={cn(
              'text-gray-100',
              onOpenResearch && 'hover:text-[var(--brand-400)] cursor-pointer transition-colors'
            )}
            onClick={(e) => {
              if (!onOpenResearch) return;
              e.preventDefault();
              e.stopPropagation();
              onOpenResearch();
            }}
            role={onOpenResearch ? 'button' : undefined}
          >
            {name}
          </span>
          {throws && <span className="text-[10px] font-medium text-gray-400 ml-1">({throws})</span>}
        </div>
        {stats ? (
          <div className="flex items-center gap-x-2.5 gap-y-0.5 mt-1.5 text-[11px] font-mono flex-wrap">
            <span className="inline-flex items-center gap-1">
              <span className="text-gray-400">{stats.fip != null ? 'FIP' : 'ERA'}</span>
              <span className={eraColor(stats.fip ?? stats.era)}>{(stats.fip ?? stats.era).toFixed(2)}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="text-gray-400">K/9</span>
              <span className={kPer9Color(stats.k_per_9)}>{stats.k_per_9.toFixed(1)}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="text-gray-400">WHIP</span>
              <span className={whipColor(stats.whip)}>{stats.whip.toFixed(2)}</span>
            </span>
          </div>
        ) : (
          <div className="text-[11px] text-gray-400">—</div>
        )}
      </div>
    </div>
  );
}

function DesktopGameCard({ game, weather, homeBatters, awayBatters, homeLineupStatus, awayLineupStatus, resolvePitcherTarget, excludedFromBestLine, onVenueClick, onOpenResearch, onBatterClick }: { game: SlateGame; weather: GameWeather | null; homeBatters: MatchupBatter[]; awayBatters: MatchupBatter[]; homeLineupStatus?: 'Projected' | 'Confirmed'; awayLineupStatus?: 'Projected' | 'Confirmed'; resolvePitcherTarget: (pitcher: NonNullable<SlateGame['home']['pitcher']>, team: string, opponent: string, gameDate: string) => ResearchTarget; excludedFromBestLine: ReadonlyArray<string>; onVenueClick: (abbr: string) => void; onOpenResearch: (target: ResearchTarget) => void; onBatterClick?: (batter: MatchupBatter, teamAbbr: string) => void }) {
  const gameDate = utcToEasternDate(game.gameTime);
  const v = game.venue;
  const { isHighElevation, isDome, isTurf, isHitterPark, isPitcherPark } = getVenueFlags(game);
  const narrativeParts = buildNarrative(game);
  const { ml, rl, total } = resolveGameLines(game.odds, excludedFromBestLine);

  return (
    <div className="w-[calc(25%-12px)] min-w-[320px] flex-shrink-0 snap-start bg-white dark:bg-bg-secondary rounded-xl border border-border/30 dark:border-border-secondary overflow-hidden hover:border-border/60 dark:hover:border-border-secondary transition-colors flex flex-col">
      {/* Top bar */}
      <div className="px-3 py-1.5 bg-muted/30 dark:bg-bg-tertiary/50 border-b border-border/20 dark:border-border-secondary">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] font-semibold text-muted-foreground dark:text-gray-300 flex-shrink-0">{formatEtTime(game.gameTime)}</span>
            {weather && <WeatherStrip weather={weather} />}
          </div>
          <div className="flex items-center gap-1 flex-wrap justify-end">
            {isHighElevation && <VenueTag color="bg-amber-500/10 text-amber-600 dark:text-amber-400"><Mountain className="w-2.5 h-2.5" />{v.elevation?.toLocaleString()}ft</VenueTag>}
            {isDome && <VenueTag color="bg-blue-500/10 text-blue-600 dark:text-blue-400">{v.roof}</VenueTag>}
            {isTurf && <VenueTag color="bg-purple-500/10 text-purple-600 dark:text-purple-400">Turf</VenueTag>}
            {isHitterPark && <VenueTag color="bg-red-500/10 text-red-600 dark:text-red-400">Hitter&apos;s Park</VenueTag>}
            {isPitcherPark && <VenueTag color="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">Pitcher&apos;s Park</VenueTag>}
            {game.status !== 'Scheduled' && game.status !== 'Pre-Game' && (
              <VenueTag color="bg-muted/50 dark:bg-white/5 text-muted-foreground dark:text-gray-400">{game.status}</VenueTag>
            )}
          </div>
        </div>
      </div>

      {/* Matchup */}
      <div className="px-4 pt-4 pb-2 flex-1">
        {/* Away */}
        <div className="flex items-start gap-4">
          <div className="flex flex-col items-center gap-0.5 flex-shrink-0 w-11">
            <TeamLogo team={game.away.abbr} sport="mlb" className="w-9 h-9" />
            <span className="text-[11px] font-bold text-foreground dark:text-gray-100 leading-tight">{game.away.abbr}</span>
            {game.away.record && <span className="text-[10px] text-gray-300 font-mono leading-none">{game.away.record.wins}-{game.away.record.losses}</span>}
          </div>
          <div className="flex-1 min-w-0 ml-1">
            {game.away.pitcher
              ? <PitcherCard
                  name={game.away.pitcher.name}
                  id={game.away.pitcher.id}
                  throws={game.away.pitcher.throws}
                  record={game.away.pitcher.record}
                  stats={game.away.pitcher.stats}
                  onOpenResearch={() => onOpenResearch(resolvePitcherTarget(game.away.pitcher!, game.away.abbr, game.home.abbr, gameDate))}
                />
              : <span className="text-[10px] px-2 py-0.5 rounded bg-muted/30 dark:bg-bg-tertiary/50 text-muted-foreground/40 dark:text-gray-400">TBD</span>}
            {(ml?.away || rl?.away) && (
              <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-1.5 ml-[54px] text-[11px] font-mono">
                {ml?.away && game.odds?.moneyline && (
                  <MarketOddsPopover
                    title={`${game.away.abbr} Moneyline`}
                    quotes={game.odds.moneyline.away}
                    excludedFromBestLine={excludedFromBestLine}
                    preference={ML_PREF}
                    trigger={
                      <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-500/10 hover:bg-gray-500/20 cursor-pointer transition-colors">
                        <span className="text-gray-400">ML</span>
                        <BookIcon bookKey={ml.away.book} />
                        <span className="text-gray-100">{formatOdds(ml.away.price)}</span>
                      </div>
                    }
                  />
                )}
                {rl?.away && game.odds?.runLine && (
                  <MarketOddsPopover
                    title={`${game.away.abbr} Run Line`}
                    quotes={game.odds.runLine.away}
                    excludedFromBestLine={excludedFromBestLine}
                    preference={RL_PREF}
                    formatPoint={formatSpread}
                    trigger={
                      <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-500/10 hover:bg-gray-500/20 cursor-pointer transition-colors">
                        <span className="text-gray-400">RL</span>
                        <BookIcon bookKey={rl.away.book} />
                        <span className="text-gray-100">{rl.awayPoint != null ? formatSpread(rl.awayPoint) : ''}</span>
                        <span className="text-gray-100">{formatOdds(rl.away.price)}</span>
                      </div>
                    }
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* O/U divider */}
        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 border-t border-border/30 dark:border-border-secondary" />
          {total?.over && total?.under && game.odds?.total ? (
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-muted/30 dark:bg-bg-tertiary/50 border border-border/20 dark:border-border-secondary flex-shrink-0">
              <span className="text-[9px] text-gray-400">O/U</span>
              <span className="text-[13px] text-gray-100 font-bold font-mono">{total.point}</span>
              <span className="text-gray-400">|</span>
              <MarketOddsPopover
                title="Total — Over"
                quotes={game.odds.total.over}
                excludedFromBestLine={excludedFromBestLine}
                preference={TOTAL_OVER_PREF}
                consensusPoint={total.point}
                altBand={0.5}
                formatPoint={(p) => p.toString()}
                trigger={
                  <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-500/10 hover:bg-gray-500/20 cursor-pointer transition-colors">
                    <BookIcon bookKey={total.over.book} />
                    <span className="text-[11px] text-gray-100 font-mono">O {formatOdds(total.over.price)}</span>
                  </div>
                }
              />
              <MarketOddsPopover
                title="Total — Under"
                quotes={game.odds.total.under}
                excludedFromBestLine={excludedFromBestLine}
                preference={TOTAL_UNDER_PREF}
                consensusPoint={total.point}
                altBand={0.5}
                formatPoint={(p) => p.toString()}
                trigger={
                  <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-500/10 hover:bg-gray-500/20 cursor-pointer transition-colors">
                    <BookIcon bookKey={total.under.book} />
                    <span className="text-[11px] text-gray-100 font-mono">U {formatOdds(total.under.price)}</span>
                  </div>
                }
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-muted/30 dark:bg-bg-tertiary/50 border border-border/20 dark:border-border-secondary flex-shrink-0">
              <span className="text-[9px] text-gray-400">O/U</span>
              <span className="text-[13px] text-gray-400 font-bold font-mono">—</span>
            </div>
          )}
          <div className="flex-1 border-t border-border/30 dark:border-border-secondary" />
        </div>

        {/* Home */}
        <div className="flex items-start gap-4">
          <div className="flex flex-col items-center gap-0.5 flex-shrink-0 w-11">
            <TeamLogo team={game.home.abbr} sport="mlb" className="w-9 h-9" />
            <span className="text-[11px] font-bold text-foreground dark:text-gray-100 leading-tight">{game.home.abbr}</span>
            {game.home.record && <span className="text-[10px] text-gray-300 font-mono leading-none">{game.home.record.wins}-{game.home.record.losses}</span>}
          </div>
          <div className="flex-1 min-w-0 ml-1">
            {game.home.pitcher
              ? <PitcherCard
                  name={game.home.pitcher.name}
                  id={game.home.pitcher.id}
                  throws={game.home.pitcher.throws}
                  record={game.home.pitcher.record}
                  stats={game.home.pitcher.stats}
                  onOpenResearch={() => onOpenResearch(resolvePitcherTarget(game.home.pitcher!, game.home.abbr, game.away.abbr, gameDate))}
                />
              : <span className="text-[10px] px-2 py-0.5 rounded bg-muted/30 dark:bg-bg-tertiary/50 text-muted-foreground/40 dark:text-gray-400">TBD</span>}
            {(ml?.home || rl?.home) && (
              <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-1.5 ml-[54px] text-[11px] font-mono">
                {ml?.home && game.odds?.moneyline && (
                  <MarketOddsPopover
                    title={`${game.home.abbr} Moneyline`}
                    quotes={game.odds.moneyline.home}
                    excludedFromBestLine={excludedFromBestLine}
                    preference={ML_PREF}
                    trigger={
                      <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-500/10 hover:bg-gray-500/20 cursor-pointer transition-colors">
                        <span className="text-gray-400">ML</span>
                        <BookIcon bookKey={ml.home.book} />
                        <span className="text-gray-100">{formatOdds(ml.home.price)}</span>
                      </div>
                    }
                  />
                )}
                {rl?.home && game.odds?.runLine && (
                  <MarketOddsPopover
                    title={`${game.home.abbr} Run Line`}
                    quotes={game.odds.runLine.home}
                    excludedFromBestLine={excludedFromBestLine}
                    preference={RL_PREF}
                    formatPoint={formatSpread}
                    trigger={
                      <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-500/10 hover:bg-gray-500/20 cursor-pointer transition-colors">
                        <span className="text-gray-400">RL</span>
                        <BookIcon bookKey={rl.home.book} />
                        <span className="text-gray-100">{rl.homePoint != null ? formatSpread(rl.homePoint) : ''}</span>
                        <span className="text-gray-100">{formatOdds(rl.home.price)}</span>
                      </div>
                    }
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Park section */}
      <div
        className="border-t border-border/20 dark:border-border-secondary p-3 cursor-pointer hover:bg-muted/20 dark:hover:bg-bg-tertiary/50 transition-colors"
        onClick={() => onVenueClick(game.home.abbr)}
      >
        <div className="flex gap-2 items-center overflow-hidden">
          <ParkDiagram dimensions={v.dimensions} className="w-40 h-36 flex-shrink-0 -mt-10" />
          <div className="flex-1 min-w-0">
            <span className="text-[9px] text-gray-400 dark:text-gray-400 font-semibold mb-1 block text-center">Park Factors</span>
            {game.parkFactors && <ParkFactorBars factors={game.parkFactors} />}
            <div className="flex items-center gap-2.5 mt-1.5">
              <div className="flex items-center gap-1">
                <div className="w-2 h-1.5 rounded-sm bg-red-500/60" />
                <span className="text-[8px] text-gray-300">Hitter</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-1.5 rounded-sm bg-emerald-500/60" />
                <span className="text-[8px] text-gray-300">Pitcher</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-2 pt-2 border-t border-border/10 dark:border-border-secondary/50 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground dark:text-gray-400 min-w-0">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{v.name}</span>
            {v.capacity && (
              <>
                <span className="text-muted-foreground/30 dark:text-gray-400">·</span>
                <Users className="w-3 h-3 flex-shrink-0" />
                <span className="flex-shrink-0">{v.capacity.toLocaleString()}</span>
              </>
            )}
          </div>
          {narrativeParts.length > 0 && (
            <div className="relative group flex-shrink-0 ml-2">
              <Info className="w-3 h-3 text-gray-400 cursor-help" />
              <div className="absolute bottom-full right-0 mb-1.5 w-52 p-2 rounded-lg bg-gray-900 border border-white/10 shadow-xl opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50">
                <p className="text-[10px] text-gray-300 leading-relaxed">
                  {narrativeParts.join(' · ')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {(homeBatters.length > 0 || awayBatters.length > 0) && (
        <div className="border-t border-border/20 dark:border-border-secondary p-3">
          <PlayerDirectory
            homeAbbr={game.home.abbr}
            awayAbbr={game.away.abbr}
            homeBatters={homeBatters}
            awayBatters={awayBatters}
            homeLineupStatus={homeLineupStatus}
            awayLineupStatus={awayLineupStatus}
            onBatterClick={onBatterClick}
            density="compact"
          />
        </div>
      )}
    </div>
  );
}

function ScrollRow({ label, games, excludedFromBestLine, getWeather, getBatters, getLineupStatus, resolvePitcherTarget, onVenueClick, onOpenResearch, onBatterClick }: { label: string; games: SlateGame[]; excludedFromBestLine: ReadonlyArray<string>; getWeather: (homeAbbr: string | undefined, iso: string | undefined) => GameWeather | null; getBatters: (abbr: string, gameDate: string) => MatchupBatter[]; getLineupStatus: (abbr: string, gameDate: string) => 'Projected' | 'Confirmed' | undefined; resolvePitcherTarget: (pitcher: NonNullable<SlateGame['home']['pitcher']>, team: string, opponent: string, gameDate: string) => ResearchTarget; onVenueClick: (abbr: string) => void; onOpenResearch: (target: ResearchTarget) => void; onBatterClick?: (batter: MatchupBatter, teamAbbr: string) => void }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [totalVisible, setTotalVisible] = useState(4);

  const scroll = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const cardWidth = scrollRef.current.querySelector(':scope > *')?.clientWidth ?? 300;
    scrollRef.current.scrollBy({ left: dir === 'right' ? cardWidth + 16 : -(cardWidth + 16), behavior: 'smooth' });
  };

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector(':scope > *')?.clientWidth ?? 300;
    const visible = Math.round(el.clientWidth / (cardWidth + 16));
    setTotalVisible(Math.max(visible, 1));
    setActiveIndex(Math.round(el.scrollLeft / (cardWidth + 16)));
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  const totalDots = Math.max(games.length - totalVisible + 1, 1);
  const showDots = games.length > totalVisible;

  return (
    <div>
      <div className="flex items-center justify-between mb-3 px-1">
        <h3 className="text-sm font-semibold text-foreground dark:text-gray-300">
          {label}
          <span className="text-xs font-normal text-muted-foreground dark:text-gray-400 ml-2">
            {games.length} game{games.length !== 1 ? 's' : ''}
          </span>
        </h3>
        <div className="flex items-center gap-2">
          {showDots && (
            <div className="flex items-center gap-1">
              {Array.from({ length: totalDots }, (_, i) => (
                <div
                  key={i}
                  className={cn(
                    'w-1 h-1 rounded-full transition-colors',
                    i === Math.min(activeIndex, totalDots - 1) ? 'bg-gray-400' : 'bg-gray-700'
                  )}
                />
              ))}
            </div>
          )}
          <button
            onClick={() => scroll('left')}
            className="p-1.5 rounded-lg hover:bg-muted/50 dark:hover:bg-white/5 text-muted-foreground dark:text-gray-400 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => scroll('right')}
            className="p-1.5 rounded-lg hover:bg-muted/50 dark:hover:bg-white/5 text-muted-foreground dark:text-gray-400 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2"
      >
        {games.map((game) => {
          const gameDate = utcToEasternDate(game.gameTime);
          return (
            <DesktopGameCard
              key={game.gamePk}
              game={game}
              weather={getWeather(game.home.abbr, game.gameTime)}
              homeBatters={getBatters(game.home.abbr, gameDate)}
              awayBatters={getBatters(game.away.abbr, gameDate)}
              homeLineupStatus={getLineupStatus(game.home.abbr, gameDate)}
              awayLineupStatus={getLineupStatus(game.away.abbr, gameDate)}
              resolvePitcherTarget={resolvePitcherTarget}
              excludedFromBestLine={excludedFromBestLine}
              onVenueClick={onVenueClick}
              onOpenResearch={onOpenResearch}
              onBatterClick={onBatterClick}
            />
          );
        })}
      </div>
    </div>
  );
}

// ─── Mobile components ──────────────────────────────────────

function MobilePitcherInfo({ pitcher, onOpenResearch }: {
  pitcher: NonNullable<SlateGame['home']['pitcher']>;
  onOpenResearch: () => void;
}) {
  const url = pitcher.id ? getPlayerImageUrl(pitcher.name, 'mlb', undefined, String(pitcher.id)) : null;
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="relative w-9 h-9 flex-shrink-0">
        <div className="w-9 h-9 rounded-full overflow-hidden bg-bg-tertiary border border-white/10">
          {url ? (
            <Image src={url} alt={pitcher.name} width={36} height={36} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-gray-400">
              {pitcher.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </div>
          )}
        </div>
        {pitcher.record && (
          <span className="absolute left-1/2 -translate-x-1/2 -bottom-3 text-[9px] font-mono text-gray-400 leading-none whitespace-nowrap">
            {pitcher.record.wins}-{pitcher.record.losses}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <span
            className="text-[12px] font-semibold text-gray-100 truncate cursor-pointer hover:text-[var(--brand-400)] transition-colors"
            onClick={(e) => { e.stopPropagation(); onOpenResearch(); }}
            role="button"
          >
            {pitcher.name}
          </span>
          {pitcher.throws && <span className="text-[9px] text-gray-400 flex-shrink-0">({pitcher.throws})</span>}
        </div>
        {pitcher.stats ? (
          <div className="flex items-center gap-x-2.5 gap-y-0.5 mt-1 text-[10px] font-mono flex-wrap">
            <span className="inline-flex items-center gap-1">
              <span className="text-gray-400">{pitcher.stats.fip != null ? 'FIP' : 'ERA'}</span>
              <span className={eraColor(pitcher.stats.fip ?? pitcher.stats.era)}>{(pitcher.stats.fip ?? pitcher.stats.era).toFixed(2)}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="text-gray-400">K/9</span>
              <span className={kPer9Color(pitcher.stats.k_per_9)}>{pitcher.stats.k_per_9.toFixed(1)}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="text-gray-400">WHIP</span>
              <span className={whipColor(pitcher.stats.whip)}>{pitcher.stats.whip.toFixed(2)}</span>
            </span>
          </div>
        ) : (
          <div className="text-[10px] text-gray-400">—</div>
        )}
      </div>
    </div>
  );
}

function MobileTeamRow({ side, game, resolved, excludedFromBestLine, resolvePitcherTarget, onOpenResearch }: {
  side: 'home' | 'away';
  game: SlateGame;
  resolved: ResolvedGameLines;
  excludedFromBestLine: ReadonlyArray<string>;
  resolvePitcherTarget: (pitcher: NonNullable<SlateGame['home']['pitcher']>, team: string, opponent: string, gameDate: string) => ResearchTarget;
  onOpenResearch: (target: ResearchTarget) => void;
}) {
  const team = game[side];
  const opponent = side === 'home' ? game.away : game.home;
  const gameDate = utcToEasternDate(game.gameTime);
  const mlBest = resolved.ml?.[side];
  const rlBest = resolved.rl?.[side];
  const mlQuotes = side === 'home' ? game.odds?.moneyline?.home : game.odds?.moneyline?.away;
  const rlQuotes = side === 'home' ? game.odds?.runLine?.home : game.odds?.runLine?.away;

  return (
    <div className="space-y-1.5">
      {/* Team + pitcher row */}
      <div className="flex items-start gap-4">
        <div className="flex flex-col items-center w-[44px] flex-shrink-0 pt-0.5">
          <TeamLogo team={team.abbr} sport="mlb" className="w-8 h-8" />
          <span className="text-[10px] font-bold text-gray-100 mt-0.5">{team.abbr}</span>
          {team.record && <div className="text-[9px] text-gray-400 font-mono leading-none">{team.record.wins}-{team.record.losses}</div>}
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          {team.pitcher
            ? <MobilePitcherInfo
                pitcher={team.pitcher}
                onOpenResearch={() => onOpenResearch(resolvePitcherTarget(team.pitcher!, team.abbr, opponent.abbr, gameDate))}
              />
            : <span className="text-[9px] px-1.5 py-0.5 rounded bg-bg-tertiary/50 text-gray-400">TBD</span>}
          {/* Odds inline under pitcher stats (aligned via avatar width + gap) */}
          {(mlBest || rlBest) && (
            <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 ml-[44px] text-[10px] font-mono">
              {mlBest && mlQuotes && (
                <MarketOddsPopover
                  title={`${team.abbr} Moneyline`}
                  quotes={mlQuotes}
                  excludedFromBestLine={excludedFromBestLine}
                  preference={ML_PREF}
                  trigger={
                    <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-500/10 hover:bg-gray-500/20 cursor-pointer transition-colors">
                      <span className="text-gray-400">ML</span>
                      <BookIcon bookKey={mlBest.book} />
                      <span className="text-gray-100">{formatOdds(mlBest.price)}</span>
                    </div>
                  }
                />
              )}
              {rlBest && rlQuotes && (
                <MarketOddsPopover
                  title={`${team.abbr} Run Line`}
                  quotes={rlQuotes}
                  excludedFromBestLine={excludedFromBestLine}
                  preference={RL_PREF}
                  formatPoint={formatSpread}
                  trigger={
                    <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-500/10 hover:bg-gray-500/20 cursor-pointer transition-colors text-gray-100">
                      <span className="text-gray-400">RL</span>
                      <BookIcon bookKey={rlBest.book} />
                      <span>{rlBest.point != null ? formatSpread(rlBest.point) : ''}</span>
                      <span>{formatOdds(rlBest.price)}</span>
                    </div>
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ParkFactorChip({ label, value }: { label: string; value: number }) {
  const dev = value - 100;
  const absDev = Math.abs(dev);
  const isHitterFriendly = label === 'K' ? dev < 0 : dev > 0;
  return (
    <div className="flex items-center gap-0.5">
      <span className="text-[9px] text-gray-400 font-medium">{label}</span>
      <span className={cn(
        'text-[9px] font-mono font-semibold',
        absDev <= 2 ? 'text-gray-400' : isHitterFriendly ? 'text-red-400' : 'text-emerald-400'
      )}>
        {dev === 0 ? '0' : (dev > 0 ? '+' : '') + dev}
      </span>
    </div>
  );
}

function MobileGameCard({ game, weather, homeBatters, awayBatters, homeLineupStatus, awayLineupStatus, resolvePitcherTarget, excludedFromBestLine, onVenueClick, onOpenResearch, onBatterClick }: { game: SlateGame; weather: GameWeather | null; homeBatters: MatchupBatter[]; awayBatters: MatchupBatter[]; homeLineupStatus?: 'Projected' | 'Confirmed'; awayLineupStatus?: 'Projected' | 'Confirmed'; resolvePitcherTarget: (pitcher: NonNullable<SlateGame['home']['pitcher']>, team: string, opponent: string, gameDate: string) => ResearchTarget; excludedFromBestLine: ReadonlyArray<string>; onVenueClick: (abbr: string) => void; onOpenResearch: (target: ResearchTarget) => void; onBatterClick?: (batter: MatchupBatter, teamAbbr: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const v = game.venue;
  const { isHighElevation, isDome, isTurf, isHitterPark, isPitcherPark } = getVenueFlags(game);
  const resolved = resolveGameLines(game.odds, excludedFromBestLine);
  const total = resolved.total;

  // Average park factors for chips
  const pf = game.parkFactors;
  const avgFactors = pf ? {
    hr: Math.round((pf.lhb.hr + pf.rhb.hr) / 2),
    h: Math.round((pf.lhb.h + pf.rhb.h) / 2),
    runs: Math.round((pf.lhb.runs + pf.rhb.runs) / 2),
    so: Math.round((pf.lhb.so + pf.rhb.so) / 2),
    bb: Math.round((pf.lhb.bb + pf.rhb.bb) / 2),
  } : null;

  return (
    <div className="bg-bg-secondary rounded-xl border border-border-secondary overflow-hidden">
      {/* Header: time + venue + tags */}
      <div className="px-3 py-2 bg-bg-tertiary/50 border-b border-border-secondary">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-[11px] font-semibold text-gray-200 flex-shrink-0">{formatEtTime(game.gameTime)}</span>
            {weather && <WeatherStrip weather={weather} />}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {isHighElevation && <VenueTag color="bg-amber-500/10 text-amber-400"><Mountain className="w-2.5 h-2.5" />{v.elevation?.toLocaleString()}ft</VenueTag>}
            {isDome && <VenueTag color="bg-blue-500/10 text-blue-400">{v.roof === 'Retractable' ? 'Retract.' : v.roof}</VenueTag>}
            {isTurf && <VenueTag color="bg-purple-500/10 text-purple-400">Turf</VenueTag>}
            {isHitterPark && <VenueTag color="bg-red-500/10 text-red-400">Hitter&apos;s Park</VenueTag>}
            {isPitcherPark && <VenueTag color="bg-emerald-500/10 text-emerald-400">Pitcher&apos;s Park</VenueTag>}
          </div>
        </div>
      </div>

      {/* Matchup section */}
      <div className="px-3 py-2.5 space-y-2">
        <MobileTeamRow side="away" game={game} resolved={resolved} excludedFromBestLine={excludedFromBestLine} resolvePitcherTarget={resolvePitcherTarget} onOpenResearch={onOpenResearch} />

        {/* O/U divider */}
        <div className="flex items-center gap-2 py-0.5">
          <div className="flex-1 border-t border-border-secondary" />
          {total?.over && total?.under && game.odds?.total ? (
            <div className="flex items-center gap-1.5 text-[10px] font-mono text-gray-300 flex-shrink-0">
              <span>O/U <span className="text-gray-100 font-bold">{total.point}</span></span>
              <MarketOddsPopover
                title="Total — Over"
                quotes={game.odds.total.over}
                excludedFromBestLine={excludedFromBestLine}
                preference={TOTAL_OVER_PREF}
                consensusPoint={total.point}
                altBand={0.5}
                formatPoint={(p) => p.toString()}
                trigger={
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-500/10 hover:bg-gray-500/20 cursor-pointer transition-colors text-gray-100">
                    <BookIcon bookKey={total.over.book} />
                    <span>O {formatOdds(total.over.price)}</span>
                  </span>
                }
              />
              <MarketOddsPopover
                title="Total — Under"
                quotes={game.odds.total.under}
                excludedFromBestLine={excludedFromBestLine}
                preference={TOTAL_UNDER_PREF}
                consensusPoint={total.point}
                altBand={0.5}
                formatPoint={(p) => p.toString()}
                trigger={
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-500/10 hover:bg-gray-500/20 cursor-pointer transition-colors text-gray-100">
                    <BookIcon bookKey={total.under.book} />
                    <span>U {formatOdds(total.under.price)}</span>
                  </span>
                }
              />
            </div>
          ) : (
            <span className="text-[10px] font-mono text-gray-400 flex-shrink-0">O/U —</span>
          )}
          <div className="flex-1 border-t border-border-secondary" />
        </div>

        <MobileTeamRow side="home" game={game} resolved={resolved} excludedFromBestLine={excludedFromBestLine} resolvePitcherTarget={resolvePitcherTarget} onOpenResearch={onOpenResearch} />
      </div>

      {/* Park factors footer — tap to expand, tap expanded to open modal */}
      {avgFactors && (
        <div className="border-t border-border-secondary bg-bg-tertiary/30">
          <div
            className="px-3 py-1.5 cursor-pointer active:bg-bg-tertiary/50 transition-colors"
            onClick={() => setExpanded(!expanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="text-[9px] text-gray-400 font-medium">Park Factors:</span>
                <ParkFactorChip label="HR" value={avgFactors.hr} />
                <ParkFactorChip label="H" value={avgFactors.h} />
                <ParkFactorChip label="R" value={avgFactors.runs} />
                <ParkFactorChip label="K" value={avgFactors.so} />
                <ParkFactorChip label="BB" value={avgFactors.bb} />
              </div>
              <ChevronDown className={cn('w-3 h-3 text-gray-400 transition-transform', expanded && 'rotate-180')} />
            </div>
          </div>

          {expanded && (
            <div
              className="px-3 pb-2 cursor-pointer active:bg-bg-tertiary/50 transition-colors"
              onClick={() => onVenueClick(game.home.abbr)}
            >
              <div className="flex gap-3 items-start">
                <ParkDiagram dimensions={v.dimensions} className="w-32 h-28 flex-shrink-0 -mt-6" />
                <div className="flex-1 min-w-0">
                  {game.parkFactors && <ParkFactorBars factors={game.parkFactors} />}
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center gap-0.5">
                      <div className="w-1.5 h-1.5 rounded-sm bg-red-500/60" />
                      <span className="text-[7px] text-gray-300">Hitter</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <div className="w-1.5 h-1.5 rounded-sm bg-emerald-500/60" />
                      <span className="text-[7px] text-gray-300">Pitcher</span>
                    </div>
                  </div>
                  <span className="text-[8px] text-gray-400 mt-1 block">Tap for full park details</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {(homeBatters.length > 0 || awayBatters.length > 0) && (
        <div className="border-t border-border-secondary px-3 py-2 bg-bg-tertiary/30">
          <PlayerDirectory
            homeAbbr={game.home.abbr}
            awayAbbr={game.away.abbr}
            homeBatters={homeBatters}
            awayBatters={awayBatters}
            homeLineupStatus={homeLineupStatus}
            awayLineupStatus={awayLineupStatus}
            onBatterClick={onBatterClick}
            density="compact"
          />
        </div>
      )}
    </div>
  );
}

// ─── Main export ─────────────────────────────────────────────

export function SlateView() {
  const { data, isLoading, error } = useSlateData();
  const { getWeather } = useMlbWeather(true);
  const { lineupByTeam, playersById } = useSlatePlayers('mlb');
  const { preferences } = useUserPreferences();
  const excludedFromBestLine = preferences?.excluded_from_best_line ?? [];
  const [venueModal, setVenueModal] = useState<string | null>(null);
  const [researchTarget, setResearchTarget] = useState<ResearchTarget | null>(null);
  // Mount the overlay only once the user opens it for the first time. After
  // that we keep it mounted so the lazy chunk stays loaded and Vaul's exit
  // animation runs on close.
  const [overlayMounted, setOverlayMounted] = useState(false);
  const sport = useSport();
  // surface='slate' distinguishes opens from the slate view vs the props grid in
  // analytics. No client-side dedup — analyst-side math handles repeat-open semantics.
  const openResearch = useCallback((t: ResearchTarget) => {
    setOverlayMounted(true);
    setResearchTarget(t);
    captureEvent(EVENTS.RESEARCH_OVERLAY_OPENED, {
      sport,
      player_id: t.playerId,
      player_name: t.playerName,
      team: t.team,
      market: t.marketCode ?? null,
      surface: 'slate',
    });
  }, [sport]);

  // Shared lookup the slate's three swap paths (overlay-swap, pitcher-card-
  // click, batter-row-click) all use as the `resolveSwapTarget` lookup arg.
  // `playersById` is the slate's id-keyed odds payload, so this hits for any
  // player whose props are currently ingested; otherwise the swap falls back
  // to the caller-provided shape.
  const lookupPlayer = useCallback((id: string) => playersById.get(id), [playersById]);

  // Overlay swap — same lookup-then-fallback policy as `/mlb` (PlayerSelectionView).
  const handleSwapTarget = useCallback((req: { playerId: string; fallback: ResearchTarget }) => {
    openResearch(resolveSwapTarget(req, lookupPlayer));
  }, [lookupPlayer, openResearch]);

  // (team, gameDate) pairs derived from the actual slate — a team playing today AND tomorrow
  // appears twice, so each game card looks up its own lineup. Deduped via a Map so we
  // don't fire the same matchup query twice in the rare same-team-same-date case.
  const matchupRequests = useMemo<TeamLineupRequest[]>(() => {
    const seen = new Map<string, TeamLineupRequest>();
    for (const game of data?.games ?? []) {
      const gameDate = utcToEasternDate(game.gameTime);
      for (const team of [game.home.abbr, game.away.abbr]) {
        const lu = lineupByTeam.get(lineupKey(team, gameDate));
        if (!lu || lu.names.length === 0) continue;
        const k = lineupKey(team, gameDate);
        if (!seen.has(k)) seen.set(k, { team, gameDate, lineup: lu.names });
      }
    }
    return [...seen.values()];
  }, [data?.games, lineupByTeam]);

  const { byTeam: battersByTeam } = useSlateMatchups(matchupRequests);

  const getBatters = useCallback(
    (abbr: string, gameDate: string) => battersByTeam.get(lineupKey(abbr, gameDate)) ?? [],
    [battersByTeam],
  );
  const getLineupStatus = useCallback(
    (abbr: string, gameDate: string) => lineupByTeam.get(lineupKey(abbr, gameDate))?.status,
    [lineupByTeam],
  );
  // Single research-target factory the slate uses for every pitcher click.
  // Goes through the canonical `resolveSwapTarget` helper so the lookup-then-
  // fallback policy is shared with the batter-click + overlay-swap paths.
  // The fallback is the slate-API row's shape (no markets/projections); the
  // lookup hits `playersById` whenever the starter's props are ingested,
  // which is almost always — pitcher markets ship every poll.
  const resolvePitcherTarget = useCallback(
    (
      pitcher: NonNullable<SlateGame['home']['pitcher']>,
      team: string,
      opponent: string,
      gameDate: string,
    ): ResearchTarget => {
      const pid = pitcher.id != null ? String(pitcher.id) : '';
      const fallback = buildPitcherFallbackTarget(
        { playerId: pid, name: pitcher.name },
        team,
        {
          opponent,
          lineup: lineupByTeam.get(lineupKey(opponent, gameDate)),
          imageUrl: pitcher.id ? getPlayerImageUrl(pitcher.name, 'mlb', undefined, String(pitcher.id)) ?? undefined : undefined,
        },
      );
      return resolveSwapTarget({ playerId: pid, fallback }, lookupPlayer);
    },
    [lookupPlayer, lineupByTeam],
  );

  // Batter row click in PlayerDirectory → research overlay. Goes through the
  // canonical `resolveSwapTarget` policy (same as the pitcher-hero swap path
  // wired in handleSwapTarget) — lookup by playerId in playersById, fall back
  // to a slate-MatchupBatter fallback when the props aren't yet ingested.
  // gameDate (used to attach the batter's own-team lineup to the fallback)
  // defaults to today's slate date.
  const handleBatterClick = useCallback(
    (batter: MatchupBatter, teamAbbr: string) => {
      if (!batter.playerId) return;
      const gameDate = utcToEasternDate(new Date().toISOString());
      const fallback = buildBatterFallbackTarget(
        { playerId: batter.playerId, name: batter.name, position: batter.position },
        teamAbbr,
        { lineup: lineupByTeam.get(lineupKey(teamAbbr, gameDate)) },
      );
      openResearch(resolveSwapTarget(
        { playerId: batter.playerId, fallback },
        lookupPlayer,
      ));
    },
    [lookupPlayer, lineupByTeam, openResearch],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground dark:text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20 text-red-500 dark:text-red-400 text-sm">
        Failed to load slate: {error.message}
      </div>
    );
  }

  const games = data?.games ?? [];

  // Group by date
  const grouped = new Map<string, { label: string; games: SlateGame[] }>();
  for (const game of games) {
    const dateKey = new Date(game.gameTime).toDateString();
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, { label: formatDateHeader(game.gameTime), games: [] });
    }
    grouped.get(dateKey)!.games.push(game);
  }

  const dateGroups = [...grouped.values()];

  return (
    <div className="h-full overflow-y-auto">
      <div className="px-2 md:px-6 pt-2 pb-4">
        {games.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground dark:text-text-tertiary text-sm">
            No upcoming games
          </div>
        ) : (
          <>
            {/* Mobile: vertical card list */}
            <div className="md:hidden space-y-6">
              {dateGroups.map((group) => (
                <div key={group.label}>
                  <h3 className="text-sm font-semibold text-gray-200 mb-2 px-1">
                    {group.label}
                    <span className="text-xs font-normal text-gray-400 ml-2">
                      {group.games.length} game{group.games.length !== 1 ? 's' : ''}
                    </span>
                  </h3>
                  <div className="space-y-2.5">
                    {group.games.map((game) => {
                      const gameDate = utcToEasternDate(game.gameTime);
                      return (
                        <MobileGameCard
                          key={game.gamePk}
                          game={game}
                          weather={getWeather(game.home.abbr, game.gameTime)}
                          homeBatters={getBatters(game.home.abbr, gameDate)}
                          awayBatters={getBatters(game.away.abbr, gameDate)}
                          homeLineupStatus={getLineupStatus(game.home.abbr, gameDate)}
                          awayLineupStatus={getLineupStatus(game.away.abbr, gameDate)}
                          resolvePitcherTarget={resolvePitcherTarget}
                          excludedFromBestLine={excludedFromBestLine}
                          onVenueClick={setVenueModal}
                          onOpenResearch={openResearch}
                          onBatterClick={handleBatterClick}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: horizontal scroll rows */}
            <div className="hidden md:block space-y-8">
              {dateGroups.map((group) => (
                <ScrollRow
                  key={group.label}
                  label={group.label}
                  games={group.games}
                  excludedFromBestLine={excludedFromBestLine}
                  getWeather={getWeather}
                  getBatters={getBatters}
                  getLineupStatus={getLineupStatus}
                  resolvePitcherTarget={resolvePitcherTarget}
                  onVenueClick={setVenueModal}
                  onOpenResearch={openResearch}
                  onBatterClick={handleBatterClick}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {venueModal && (
        <VenueDetailModal
          teamAbbr={venueModal}
          onClose={() => setVenueModal(null)}
          dimensions={games.find(g => g.home.abbr === venueModal)?.venue.dimensions}
        />
      )}

      {overlayMounted && (
        <PlayerResearchOverlay
          target={researchTarget}
          onClose={() => setResearchTarget(null)}
          sport={sport}
          onSwapTarget={handleSwapTarget}
        />
      )}
    </div>
  );
}
